import type { SalesforceRecordsInput } from "../generated/graphql";
import { SalesforcePeriodGranularity, SalesforceRecordsKind, SalesforceSegment } from "../generated/graphql";

export const SALESFORCE_UNITY_PALETTE: readonly string[] = [
  "#3296ed",
  "#77b9f2",
  "#9d53f2",
  "#c398f5",
  "#26aba4",
  "#4ed4cd",
  "#f7a452",
  "#faca9b",
  "#f2536d",
  "#f598a7",
  "#96d44e",
  "#c7f296",
  "#537bf2",
  "#7796f2",
  "#f253d2",
  "#f598e2",
  "#edbe32",
  "#f5de98",
  "#4ed469",
  "#7ceb92",
];

export const SALESFORCE_CHART_COLORS = {
  cardBackground: "#ffffff",
  cardBorder: "#c9c9c9",
  cardShadow: "0 2px 3px 0 rgba(0,0,0,0.16)",
  headerBackground: "#f3f3f3",
  text: "#181818",
  textStrong: "#080707",
  textWeak: "#706e6b",
  tick: "#3e3e3c",
  grid: "rgba(197,211,223,0.5)",
  link: "#0176d3",
  warning: "#dd7a01",
  singleSeries: "#3296ed",
  sliceStroke: "#ffffff",
  hoverOutline: "#000000",
  legendHover: "#e7ebf5",
  tooltipText: "#ffffff",
} as const;

export const SALESFORCE_TOOLTIP_COLORS = {
  background: "#16325c",
  label: "#9faab5",
  value: "#ffffff",
} as const;

// The hovered mark is drawn in its own colour with every channel halved, as in Salesforce.
const HOVER_DARKEN_FACTOR = 0.5;
const HEX_CHANNEL_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

export const darkenColor = (hex: string): string => {
  const match = HEX_CHANNEL_PATTERN.exec(hex);
  if (!match) return hex;
  const channels = match.slice(1).map(channel =>
    Math.round(parseInt(channel, 16) * HOVER_DARKEN_FACTOR)
      .toString(16)
      .padStart(2, "0")
  );
  return `#${channels.join("")}`;
};

export const SALESFORCE_STATUS_ORDER: readonly string[] = [
  "Pending",
  "Trial/Withdrawal",
  "Active",
  "Cancelled",
  "Ended",
];

export const SALESFORCE_STATUS_COLORS: Readonly<Record<string, string>> = {
  Pending: "#33cc00",
  "Trial/Withdrawal": "#ffcc33",
  Active: "#0b6fce",
  Cancelled: "#ff6600",
  Ended: "#660000",
};

export type CancellationTypeLabelKey =
  | "testAbort"
  | "withdrawalWithinPeriod"
  | "ordinaryCancellation"
  | "extraordinaryCancellationCustomer"
  | "cancellationByM4C"
  | "cancellationAfterFundingExpired"
  | "reductionOrder"
  | "contractChangeNeutral"
  | "testEndAfterFundingExpired"
  | "productChange";

// Picklist order of the Salesforce "Kündigungsart" field; the last two values are inactive but still occur in data.
export const SALESFORCE_CANCELLATION_TYPES: ReadonlyArray<{
  value: string;
  labelKey: CancellationTypeLabelKey;
}> = [
  { value: "Testabbruch", labelKey: "testAbort" },
  {
    value: "Widerruf innerhalb ges. Frist",
    labelKey: "withdrawalWithinPeriod",
  },
  { value: "Ordentliche Kuendigung", labelKey: "ordinaryCancellation" },
  {
    value: "SonderkuendigungKD",
    labelKey: "extraordinaryCancellationCustomer",
  },
  { value: "Kuendigung durch M4C", labelKey: "cancellationByM4C" },
  {
    value: "Kündigung nach ausgelaufener KK-Förderung",
    labelKey: "cancellationAfterFundingExpired",
  },
  { value: "Reduction Order", labelKey: "reductionOrder" },
  {
    value: "Bestandsneutrale Vertragsänderung",
    labelKey: "contractChangeNeutral",
  },
  {
    value: "Testende nach ausgelaufener KK-Förderung",
    labelKey: "testEndAfterFundingExpired",
  },
  { value: "Produktwechsel", labelKey: "productChange" },
];

export const cancellationTypeLabelKey = (value: string): CancellationTypeLabelKey | undefined =>
  SALESFORCE_CANCELLATION_TYPES.find(type => type.value === value)?.labelKey;

export type PeriodCountRow = {
  periodStart: string;
  segment: SalesforceSegment;
  category?: string | null;
  count: number;
};
export type ChartCategory = { value: string | null; color: string };
export type StackedPeriod = {
  periodStart: string;
  counts: number[];
  total: number;
};
export type StackedSeries = {
  categories: ChartCategory[];
  periods: StackedPeriod[];
};

const germanCollator = new Intl.Collator("de");

export const paletteColor = (index: number): string =>
  SALESFORCE_UNITY_PALETTE[index % SALESFORCE_UNITY_PALETTE.length];

// Legend order: blank first, then known values in picklist order, then unknown values collated.
const orderCategories = (present: ReadonlySet<string | null>, knownValues: readonly string[]): Array<string | null> => {
  const known = knownValues.filter(value => present.has(value));
  const unknown = Array.from(present)
    .filter((value): value is string => value !== null && !knownValues.includes(value))
    .sort(germanCollator.compare);
  return [...(present.has(null) ? [null] : []), ...known, ...unknown];
};

const buildSeries = (
  rows: readonly PeriodCountRow[],
  segment: SalesforceSegment,
  knownValues: readonly string[],
  colorFor: (value: string | null, legendIndex: number, ordered: ReadonlyArray<string | null>) => string
): StackedSeries => {
  const segmentRows = rows.filter(row => row.segment === segment);
  const present = new Set<string | null>(segmentRows.map(row => row.category ?? null));
  const ordered = orderCategories(present, knownValues);
  const categoryIndex = new Map<string | null, number>(ordered.map((value, index) => [value, index]));

  const countsByPeriod = new Map<string, number[]>();
  segmentRows.forEach(row => {
    const counts = countsByPeriod.get(row.periodStart) ?? ordered.map(() => 0);
    const index = categoryIndex.get(row.category ?? null);
    if (index !== undefined) {
      counts[index] += row.count;
    }
    countsByPeriod.set(row.periodStart, counts);
  });

  return {
    categories: ordered.map((value, index) => ({
      value,
      color: colorFor(value, index, ordered),
    })),
    periods: Array.from(countsByPeriod.keys())
      .sort()
      .map(periodStart => {
        const counts = countsByPeriod.get(periodStart) ?? [];
        return {
          periodStart,
          counts,
          total: counts.reduce((sum, count) => sum + count, 0),
        };
      }),
  };
};

export const buildStatusSeries = (rows: readonly PeriodCountRow[], segment: SalesforceSegment): StackedSeries =>
  buildSeries(
    rows,
    segment,
    SALESFORCE_STATUS_ORDER,
    (value, legendIndex) => (value !== null ? SALESFORCE_STATUS_COLORS[value] : undefined) ?? paletteColor(legendIndex)
  );

const CANCELLATION_TYPE_VALUES = SALESFORCE_CANCELLATION_TYPES.map(type => type.value);

// Salesforce assigns these colours dynamically per chart. The portal keeps one colour per value across all
// Kündigungsart charts (blank and the 10 picklist values); values outside the picklist take the next palette
// colours in collated order within a chart.
const CANCELLATION_KNOWN_COUNT = CANCELLATION_TYPE_VALUES.length;

export const buildCancellationTypeSeries = (
  rows: readonly PeriodCountRow[],
  segment: SalesforceSegment
): StackedSeries =>
  buildSeries(rows, segment, CANCELLATION_TYPE_VALUES, (value, _, ordered) => {
    if (value === null) {
      return paletteColor(0);
    }
    const knownIndex = CANCELLATION_TYPE_VALUES.indexOf(value);
    if (knownIndex >= 0) {
      return paletteColor(knownIndex + 1);
    }
    const unknownValues = ordered.filter(
      (candidate): candidate is string => candidate !== null && !CANCELLATION_TYPE_VALUES.includes(candidate)
    );
    return paletteColor(CANCELLATION_KNOWN_COUNT + 1 + unknownValues.indexOf(value));
  });

export const salesforceSyncedAtFormat = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Europe/Berlin",
});

export const BLANK_CATEGORY_LABEL = "-";
export const BLANK_CATEGORY_KEY = "__blank__";

// Salesforce draws the first legend entry on top; Recharts stacks the first <Bar> at the bottom, so render in reverse.
export const stackRenderOrder = (
  categories: readonly ChartCategory[]
): Array<{ category: ChartCategory; index: number }> =>
  categories.map((category, index) => ({ category, index })).reverse();

export type DonutSlice = {
  key: string;
  label: string | null;
  count: number;
  color: string;
  isOther: boolean;
  // Product names the slice stands for; more than one for "Andere".
  labels: Array<string | null>;
};
export type DonutData = { total: number; slices: DonutSlice[] };

export const sliceDisplayLabel = (slice: DonutSlice, otherLabel: string): string => {
  if (slice.isOther) return otherLabel;
  return slice.label ?? BLANK_CATEGORY_LABEL;
};

const OTHER_SLICE_KEY = "other";
const SMALL_GROUP_SHARE = 0.03;

export const buildDonutData = (
  products: ReadonlyArray<{ label?: string | null; count: number }>,
  options?: { maxGroups?: number; combineSmallGroups?: boolean }
): DonutData => {
  const total = products.reduce((sum, product) => sum + product.count, 0);
  const sorted = products
    .map(product => ({ label: product.label ?? null, count: product.count }))
    .sort((a, b) => {
      if (a.label === null || b.label === null) {
        return a.label === b.label ? 0 : a.label === null ? -1 : 1;
      }
      return germanCollator.compare(a.label, b.label);
    });

  const maxGroups = options?.maxGroups;
  const kept = maxGroups === undefined ? sorted : sorted.slice(0, maxGroups);

  type Shown = { label: string | null; count: number; isOther: boolean; labels: Array<string | null> };
  let shown: Shown[] = kept.map(product => ({ ...product, isOther: false, labels: [product.label] }));

  if (options?.combineSmallGroups) {
    const keptSum = kept.reduce((sum, product) => sum + product.count, 0);
    const small = kept.filter(product => product.count < keptSum * SMALL_GROUP_SHARE);
    if (small.length > 0) {
      shown = [
        ...kept
          .filter(product => !small.includes(product))
          .map(product => ({ ...product, isOther: false, labels: [product.label] })),
        {
          label: null,
          count: small.reduce((sum, product) => sum + product.count, 0),
          isOther: true,
          labels: small.map(product => product.label),
        },
      ];
    }
  }

  return {
    total,
    slices: shown.map((slice, index) => ({
      key: slice.isOther ? OTHER_SLICE_KEY : (slice.label ?? BLANK_CATEGORY_KEY),
      label: slice.label,
      count: slice.count,
      color: paletteColor(index),
      isOther: slice.isOther,
      labels: slice.labels,
    })),
  };
};

export type PeriodGranularity = "day" | "week" | "month";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_WEEK = 7;
const AXIS_TICK_THOUSAND = 1000;

const parsePeriodStart = (periodStart: string): Date => new Date(`${periodStart}T00:00:00Z`);

const monthShortFormat = new Intl.DateTimeFormat("de-DE", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const monthLongFormat = new Intl.DateTimeFormat("de-DE", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const countFormat = new Intl.NumberFormat("de-DE");
const oneFractionFormat = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 1,
});
// Small axis steps such as 0,35 must not be rounded into a misleading tick label.
const smallTickFormat = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 2,
});

const twoDigits = (value: number): string => String(value).padStart(2, "0");
const dayMonth = (date: Date): string => `${twoDigits(date.getUTCDate())}.${twoDigits(date.getUTCMonth() + 1)}.`;
const dayMonthYear = (date: Date): string => `${dayMonth(date)}${date.getUTCFullYear()}`;
const weekEnd = (start: Date): Date => new Date(start.getTime() + (DAYS_PER_WEEK - 1) * MS_PER_DAY);

export const formatPeriodTick = (periodStart: string, granularity: PeriodGranularity): string => {
  const start = parsePeriodStart(periodStart);
  switch (granularity) {
    case "month":
      return monthShortFormat.format(start);
    case "week":
    case "day":
      return dayMonth(start);
  }
};

export const formatPeriodLong = (periodStart: string, granularity: PeriodGranularity): string => {
  const start = parsePeriodStart(periodStart);
  switch (granularity) {
    case "month":
      return monthLongFormat.format(start);
    case "week":
      return `${dayMonthYear(start)} – ${dayMonthYear(weekEnd(start))}`;
    case "day":
      return dayMonthYear(start);
  }
};

export const formatAxisTick = (value: number): string =>
  Math.abs(value) >= AXIS_TICK_THOUSAND
    ? `${oneFractionFormat.format(value / AXIS_TICK_THOUSAND)}k`
    : smallTickFormat.format(value);

export const formatChartValue = (value: number): string => oneFractionFormat.format(value);

const THOUSAND = 1000;

// Salesforce quotes totals in the "% of 163k" line in thousands.
export const formatCompactCount = (value: number): string =>
  value >= THOUSAND ? `${countFormat.format(Math.round(value / THOUSAND))}k` : oneFractionFormat.format(value);

export const formatCount = (value: number): string => countFormat.format(value);

export const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export type SalesforceDrillDown =
  | { kind: "records"; title: string; input: SalesforceRecordsInput }
  | { kind: "contracts"; title: string; segment: SalesforceSegment };

const DRILL_DOWN_PARAM = "d";

// The records page keeps the clicked chart element in the URL, so reload, back and shared links work.
export const drillDownSearch = (drillDown: SalesforceDrillDown): string =>
  new URLSearchParams({ [DRILL_DOWN_PARAM]: JSON.stringify(drillDown) }).toString();

const isEnumValue = <T extends string>(values: Record<string, T>, value: unknown): value is T =>
  Object.values(values).includes(value as T);

const isLabels = (value: unknown): value is Array<string | null> =>
  Array.isArray(value) && value.every(label => label === null || typeof label === "string");

export const parseDrillDown = (search: string): SalesforceDrillDown | null => {
  try {
    const raw: unknown = JSON.parse(new URLSearchParams(search).get(DRILL_DOWN_PARAM) ?? "");
    if (typeof raw !== "object" || raw === null) return null;
    const value = raw as Record<string, unknown>;
    if (typeof value.title !== "string") return null;
    if (value.kind === "contracts") {
      return isEnumValue(SalesforceSegment, value.segment)
        ? { kind: "contracts", title: value.title, segment: value.segment }
        : null;
    }
    const input = value.input as Record<string, unknown> | undefined;
    if (value.kind !== "records" || !input || !isEnumValue(SalesforceRecordsKind, input.kind)) return null;
    return {
      kind: "records",
      title: value.title,
      input: {
        kind: input.kind,
        segment: isEnumValue(SalesforceSegment, input.segment) ? input.segment : undefined,
        granularity: isEnumValue(SalesforcePeriodGranularity, input.granularity) ? input.granularity : undefined,
        periodStart: typeof input.periodStart === "string" ? input.periodStart : undefined,
        labels: isLabels(input.labels) ? input.labels : undefined,
      },
    };
  } catch {
    return null;
  }
};
